import type { ActionType, Zone } from '~/@types/account';
import type { CustomerQuery } from '~/graphql/generated-customer';

const excludedCountriesFromSort = ['AT', 'DE', 'CH', 'FR', 'DK'];
export const useCountries = (language: string) => {
    const regionNames = new Intl.DisplayNames([language], { type: 'region' });

    return [
        ...excludedCountriesFromSort,
        'AD', // Andorra
        'AL', // Albania
        'BA', // Bosnia & Herzegovina
        'BE', // Belgium
        'BG', // Bulgaria
        'BY', // Belarus
        'CA', // Canada
        'CY', // Cyprus
        'CZ', // Czech Republic
        'EE', // Estonia
        'ES', // Spain
        'FI', // Finland
        'GB', // United Kingdom
        'GG', // Guernsey
        'GI', // Gibraltar
        'GR', // Greece
        'HR', // Croatia
        'HU', // Hungary
        'IE', // Ireland
        'IM', // Isle of Man
        'IS', // Iceland
        'IT', // Italy
        'JE', // Jersey
        'LI', // Liechtenstein
        'LT', // Lithuania
        'LU', // Luxembourg
        'LV', // Latvia
        'MD', // Moldova
        'ME', // Montenegro
        'MT', // Malta
        'NL', // Netherlands
        'NO', // Norway
        'PL', // Poland
        'PT', // Portugal
        'RO', // Romania
        'RS', // Serbia
        'SE', // Sweden
        'SI', // Slovenia
        'SK', // Slovakia
        'UA', // Ukraine
        'US', // United States
        'VA', // Vatican City
        'XK', // Kosovo
    ]
        .map((countryCode) => {
            return {
                value: countryCode,
                label: regionNames.of(countryCode) || countryCode,
            };
        })
        .sort((a, b) => {
            if (excludedCountriesFromSort.includes(a.value) || excludedCountriesFromSort.includes(b.value)) return 0;
            return a.label.localeCompare(b.label);
        });
};

async function getCountryZones(country: string): Promise<Zone[]> {
    country = country.toUpperCase();
    try {
        const zones = await $fetch<Zone[]>(`/api/zone-code?country=${country}`);
        return zones;
    } catch (err) {
        console.error(`Failed to load zones for ${country}:`, err);
        return [];
    }
}

export function useCountryZones(countryRef: Ref<string>) {
    return useAsyncData(
        () => `country-zones:${countryRef.value}`,
        () => (countryRef.value ? getCountryZones(countryRef.value) : Promise.resolve([])),
        {
            default: () => [] as { value: string; label: string }[],
            transform: (zones) =>
                zones.map((zone) => ({
                    value: zone.value,
                    label: zone.label,
                })),
        },
    );
}

export const useCountryZoneLabel = (territoryCode: Ref<string>, zoneCode: Ref<string>) => {
    return useAsyncData<string>(
        () => `country-zone-label:${territoryCode.value}:${zoneCode.value}`,
        async () => {
            const zones = await getCountryZones(territoryCode.value);
            const match = zones.find((zone) => zone.value === zoneCode.value);
            return match?.label ?? zoneCode.value;
        },
    );
};

export const useAccount = () => {
    const { isUS, t, locale } = useLocales();
    const runtimeConfig = useRuntimeConfig();
    const localPath = useLocalePathWoom();

    const createA11yLabel = (prefix?: string, action?: ActionType, postfix?: string) => {
        if (!prefix || !action || !postfix) return '';
        return t(`${prefix}.${action}`, { context: postfix });
    };

    const accountEnabled = computed(() => {
        const rtc = runtimeConfig.public.accountSectionEnabled;
        return rtc.includes(locale.value);
    });

    const shopifyAccountUrl = computed(
        () => `https://account-${isUS.value ? 'us' : 'eu'}${runtimeConfig.public.production ? '' : '.staging'}.woom.com/orders`,
    );

    const fetchCustomer = async () => {
        // https://nuxt.com/docs/getting-started/data-fetching#client-only-fetching
        return await useFetch<CustomerQuery['customer']>(localPath('/customer'), {
            method: 'POST',
            key: 'customer',
            lazy: true,
            server: false,
        });
    };

    return {
        accountEnabled,
        shopifyAccountUrl,
        createA11yLabel,
        fetchCustomer,
    };
};
